/*--External-- */
import _ from "lodash";
import PropTypes from "prop-types";
import classNames from "classnames";
import { Modal, Button, IconButton } from "@toddle-edu/ds-web";
import React, { useCallback, useState } from "react";
import { DeleteOutlined } from "@toddle-edu/ds-icons";

/**--Internal-- */
import Loading from "UIComponents/Loading";
import MediaPlaceholder from "UIComponents/MediaPlaceholder";
import { isWorksheetWebView } from "Worksheet/modules/WorksheetUtils";

/**--Relative--*/
import classes from "./DisplayVideoResponse.scss";
import {
  getPlaceholderAttachment,
  getVideoResponseFieldStyles,
} from "../VideoResponseFieldUtils";

/**--Styles--*/
const styles = getVideoResponseFieldStyles();

/**
 *
 * @param {*} params
 * @returns Video Display Component
 */
const DisplayVideoResponse = ({
  t,
  mode,
  attachment,
  questionId,
  deleteAttachment,
}) => {
  const attachmentId = _.get(attachment, "id", null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  // * Show confirmation dialog before deletion
  const showConfirmation = useCallback(() => {
    setShowDeleteConfirmation(true);
  }, []);

  // * Cancel delete action
  const handleCancelDelete = useCallback(() => {
    setShowDeleteConfirmation(false);
  }, []);

  // * Confirm delete action
  const handleConfirmDelete = useCallback(async () => {
    deleteAttachment({ attachmentId, nodeId: questionId });
    setShowDeleteConfirmation(false);
  }, [attachmentId, questionId, deleteAttachment]);

  if (!attachmentId && mode !== "preview") return null;

  const mediaMode = mode === "view" ? "view" : "edit";

  const deleteIconTooltip = t("common:delete_this_with_label", {
    label: t("worksheet:video_response"),
  });

  const containerStyle = attachment
    ? styles.mediaContainerStyle
    : styles.placeholderContainerStyle;

  const labelClasses = classNames("text-body-s", classes.label);
  const labelLocale = _.includes(["edit", "preview"], mode)
    ? t("common:your_response")
    : t("worksheet:student_response");

  return (
    <div
      className={classNames({
        [classes.containerWrapper]: attachment,
        [classes.containerWrapperPlaceholder]: !attachment,
      })}
    >
      {!_.isEmpty(attachment) && (
        <div className={labelClasses}>{labelLocale}</div>
      )}
      <div className={classes.relativeContainer}>
        {!attachment && (
          <Loading
            position="center"
            isMiniLoader={true}
            containerStyle={styles.loaderStyle}
          />
        )}
        {isWorksheetWebView() ? (
          <div className={classes.iPhoneActionsContainer}>
            <a href={_.get(attachment, "url")}>
              <Button dsVersion="2.0" variant="progressive" type="fill">
                {t("unitPlan:play_video")}
              </Button>
            </a>
            {mode !== "view" && (
              <IconButton
                dsVersion="2.0"
                type="outlined"
                variant="neutral"
                icon={<DeleteOutlined />}
                onClick={showConfirmation}
              />
            )}
          </div>
        ) : (
          <MediaPlaceholder
            mode={mediaMode}
            key={attachmentId}
            imageMaxWidth={556}
            imageMaxHeight={312}
            showDownloadIcon={false}
            isAttachmentCreated={true}
            deleteButtonVariant="small"
            showVideoPlayer={!!attachment}
            containerStyle={containerStyle}
            showMiddleContainerIcons={false}
            showVideoPlayerV2={!!attachment}
            deleteAttachment={showConfirmation}
            deleteIconTooltip={deleteIconTooltip}
            showDeleteIconOnTopCorner={!!attachment}
            customDeleteButtonStyle={styles.customDeleteButtonStyle}
            attachments={[attachment ? attachment : getPlaceholderAttachment()]}
            isolateSpacebarControl={true}
          />
        )}
      </div>
      {showDeleteConfirmation && (
        <Modal
          width="440px"
          height="fit-content"
          onClose={handleCancelDelete}
          isOpen={showDeleteConfirmation}
        >
          <Modal.Header
            title={t("common:delete_with_label", {
              label: t("worksheet:video_response"),
            })}
          />
          <Modal.Body>
            <div className="text-body-1">
              {t("classRoom:delete_assignment_message_with_label", {
                label: t("worksheet:video_response"),
              })}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "8px",
              }}
            >
              <Button
                dsVersion="2.0"
                onClick={handleCancelDelete}
                variant="neutral"
                type="outlined"
                size="medium"
              >
                {t("common:cancel")}
              </Button>
              <Button
                dsVersion="2.0"
                onClick={handleConfirmDelete}
                variant="primary"
                size="medium"
              >
                {t("common:delete")}
              </Button>
            </div>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
};

DisplayVideoResponse.propTypes = {
  t: PropTypes.func.isRequired,
  mode: PropTypes.string.isRequired,
  questionId: PropTypes.string.isRequired,
  attachmentId: PropTypes.string.isRequired,
  deleteAttachment: PropTypes.func.isRequired,
  updateAttachments: PropTypes.func.isRequired,
};

DisplayVideoResponse.defaultProps = {
  t: _.noop,
  mode: "view",
  questionId: "",
  attachmentId: "",
  deleteAttachment: _.noop,
  updateAttachments: _.noop,
};

export default DisplayVideoResponse;
